import DPO from 'dpo8core';
import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import LocalizedText from '@LocalizedText';
import { getMailMergeHintByKey, mergeDataToBarcode } from '@app/utils/mailMerge';
import { inject } from '@app/providers/QAU.ContextProvider';
import { setFooterConfig } from '@app/actions/footerActions';
import { processFinishedArtwork, finishArtwork } from '@app/actions/artworkDataActions';

import AutoValidateForm from '@components/auto-validate/AutoValidateForm';
import EditableSelect from '@components/editable-select/EditableSelect';
import './UploadCodesFinish.scss';

const validateBrandId = brandId => !brandId?.trim();

const UploadCodesFinish = ({
  processFinishedArtwork,
  resetSelectedFile,
  setFooterConfig,
  finishArtwork,
  updateContext,
  brandIdList,
  flowConfig,
  fileName,
  context,
  flow,
}) => {
  const [uploadFormData, setUploadFormData] = useState({});
  const [brandIdOptions, setBrandIdOptions] = useState([]);
  const [uploadFormValid, setUploadFormValid] = useState(false);
  const [initialUploadFormValues, setInitialUploadFormValues] = useState({ brand_id: '', codes_number: '' });

  const updateMailMergeInContext = useCallback(() => {
    const {
      projectData: { mailMergeData: mailMerge },
    } = context;

    const brandIdHint = getMailMergeHintByKey(mailMerge, 'brandIdentifier');
    brandIdHint.value = uploadFormData.brand_id;

    const mmData = DPO.mmd.Data.fromObject(mailMerge);
    const provider = new DPO.mmd.DataSourceProvider(mmData);

    const newContext = context.createNew(context.project, context.projectData, provider);

    updateContext(newContext);
  }, [context, uploadFormData, updateContext]);

  const removeMailMergeFromProject = useCallback(() => {
    flow.projectData.mailMergeData = null;
    const provider = new DPO.mmd.DataSourceProvider(null);
    const newContext = context.createNew(context.project, context.projectData, provider);

    updateContext(newContext);
  }, [flow, context, updateContext]);

  const populateMailMergeData = useCallback(() => {
    const { projectData: { mailMergeData: mailMerge } = {}, selector } = context;

    const { value: mmBrandId = '' } = getMailMergeHintByKey(mailMerge, 'brandIdentifier');
    const { value: mmNumberOfCodes = '' } = getMailMergeHintByKey(mailMerge, 'numberOfCodes');

    const brandsList = mmBrandId ? [mmBrandId] : brandIdList;
    const [preselectedBrandId] = brandsList;

    const [barcodeField] = selector.selectFieldsByHint('type', 'transparencyAzCodeDM');
    barcodeField && mergeDataToBarcode(barcodeField, mailMerge);

    setBrandIdOptions(brandsList);
    setInitialUploadFormValues({
      brand_id: preselectedBrandId,
      codes_number: mmNumberOfCodes,
    });
  }, [context, brandIdList]);

  const activateContextAndFinishArtwork = useCallback(() => {
    const { project, projectData, storage, provider } = context;

    flow.project = project;
    flow.projectData = projectData;
    flow.storage = storage;
    flow.provider = provider;

    flow.editor = flow.editor || {};
    flow.editor.context = context;

    finishArtwork(flow, context, flowConfig).then(artworkData => {
      setFooterConfig({ backDisabled: true });
      processFinishedArtwork(artworkData, flow, flowConfig);
    });
  }, [flow, context, flowConfig, finishArtwork, processFinishedArtwork, setFooterConfig]);

  const correctProjectAndActivateContext = useCallback(() => {
    const corrector = new DPO.project.Corrector(context);
    corrector.on('correction:finished', activateContextAndFinishArtwork, this);
    corrector.startCorrection();
  }, [context, activateContextAndFinishArtwork]);

  const updateMailMergeAndFinishArtwork = useCallback(() => {
    updateMailMergeInContext();
    correctProjectAndActivateContext();
  }, [updateMailMergeInContext, correctProjectAndActivateContext]);

  const onBrandIdChange = ({ target: { value } }) => {
    setUploadFormData({ ...uploadFormData, brand_id: value });
  };

  const onFormValidated = (errors, values) => {
    setUploadFormValid(!Object.keys(errors).length);
    setUploadFormData(values);
  };

  const navigateBackToUploadCodesStep = useCallback(() => {
    resetSelectedFile();
    removeMailMergeFromProject();
  }, [resetSelectedFile, removeMailMergeFromProject]);

  useEffect(() => {
    setFooterConfig({
      visible: true,
      backHidden: false,
      nextHidden: false,
      onBack: navigateBackToUploadCodesStep,
      onNext: updateMailMergeAndFinishArtwork,
      nextLocKey: 'qau-finish-btn',
      nextDisabled: !uploadFormValid,
    });
  }, [uploadFormData, setFooterConfig, navigateBackToUploadCodesStep, uploadFormValid, updateMailMergeAndFinishArtwork]);

  useEffect(populateMailMergeData, []);

  return (
    <div id="qau-upload-codes-finish">
      <div className="codes-file-name">
        <span>{fileName}</span>
      </div>
      <Formik enableReinitialize initialValues={initialUploadFormValues}>
        {({ errors, touched }) => (
          <Form autoComplete="off">
            <label htmlFor="trasparency-brand-id">
              <LocalizedText id="qau-apply-codes-brand-id-title" tagName="span" />
              <Field
                name="brand_id"
                id="trasparency-brand-id"
                validate={validateBrandId}
                component={EditableSelect}
                onChange={onBrandIdChange}
                optionsList={brandIdOptions}
                className={classnames('editable-select', { invalid: errors.brand_id })}
                maxLength="64"
                preselectFirst
              />
            </label>
            <label htmlFor="trasparency-codes-number">
              <LocalizedText id="qau-apply-codes-codes-number" tagName="span" />
              <Field type="text" className="read-only" name="codes_number" id="trasparency-codes-number" readOnly />
            </label>
            <AutoValidateForm onValidated={onFormValidated} />
          </Form>
        )}
      </Formik>
    </div>
  );
};

const mapStateToProps = ({
  applyCodes: {
    ATBCodesData: { originalName },
    brandIdList = [],
  },
}) => ({
  fileName: originalName,
  brandIdList,
});

const mapDispatchToProps = {
  processFinishedArtwork,
  setFooterConfig,
  finishArtwork,
};

UploadCodesFinish.propTypes = {
  brandIdList: PropTypes.array,
  flow: PropTypes.object.isRequired,
  context: PropTypes.object.isRequired,
  fileName: PropTypes.string.isRequired,
  flowConfig: PropTypes.object.isRequired,
  updateContext: PropTypes.func.isRequired,
  finishArtwork: PropTypes.func.isRequired,
  setFooterConfig: PropTypes.func.isRequired,
  resetSelectedFile: PropTypes.func.isRequired,
  processFinishedArtwork: PropTypes.func.isRequired,
};

export default inject(
  'updateContext',
  'flowConfig',
  'context',
  'flow',
)(connect(mapStateToProps, mapDispatchToProps)(UploadCodesFinish));
